import { ValidationResult, BaseValidatorOptions } from "./types";

/**
 * Options for email validation
 */
export interface EmailValidatorOptions extends BaseValidatorOptions {
  pattern?: RegExp;
}

/**
 * Validate an email value
 * @param value - The email string to validate
 * @param options - Validation options (custom pattern)
 * @returns Validation result with the email value and optional error, or null if invalid
 *
 * @example
 * ```typescript
 * const result = validateEmail("user@example.com");
 * if (result) {
 *   console.log(result.value); // "user@example.com"
 * }
 *
 * // With custom pattern
 * const result2 = validateEmail("user@company.com", {
 *   pattern: /^[a-z0-9]+@company\.com$/
 * });
 * ```
 */
export function validateEmail(value: string, options: EmailValidatorOptions = {}): ValidationResult<string> | null {
  const { field = "value", pattern } = options;

  // Basic email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!emailRegex.test(value)) {
    return null;
  }

  // Check custom pattern if provided
  if (pattern && !pattern.test(value)) {
    return {
      value,
      error: {
        field,
        message: `"${field}" does not match required email pattern: ${value}`,
        severity: "warning",
      },
    };
  }

  return { value };
}
